import type { ClientVerdict } from "@ranking-arena/shared/schemas";

export type Verdict = ClientVerdict;

interface VoteBarProps {
  onVote: (v: Verdict) => void;
  disabled: boolean;
  chosen: Verdict | null;
}

type SideItem = {
  key: Verdict;
  label: string;
  hint: string;
  side: "left" | "right";
};
type ToneItem = {
  key: Verdict;
  label: string;
  hint: string;
  tone: "good" | "bad";
};
type Item = SideItem | ToneItem;

function isSideItem(it: Item): it is SideItem {
  return "side" in it;
}

const ITEMS: Item[] = [
  { key: "left", label: "Left is better", hint: "1", side: "left" },
  { key: "both_good", label: "Both are good", hint: "2", tone: "good" },
  { key: "both_bad", label: "Both are bad", hint: "3", tone: "bad" },
  { key: "right", label: "Right is better", hint: "4", side: "right" },
];

/**
 * Four-way verdict bar. Keyboard hints (1/2/3/4) are chipped inside each
 * button; the actual keydown wiring lives in the Search page so it can
 * scope the listener to the results phase only and use `e.code` for
 * layout-independent dispatch.
 */
export function VoteBar({ onVote, disabled, chosen }: VoteBarProps) {
  return (
    <div
      role="group"
      aria-label="Verdict"
      style={{
        display: "grid",
        gridTemplateColumns: "1.1fr 1fr 1fr 1.1fr",
        gap: 1,
        border: "1px solid var(--border)",
        borderRadius: 12,
        background: "var(--border)",
        overflow: "hidden",
        boxShadow: "var(--shadow-xs)",
      }}
    >
      {ITEMS.map((it) => {
        const isChosen = chosen === it.key;
        const side = isSideItem(it);
        const accent = side
          ? "var(--atf-pink-600)"
          : it.tone === "good"
            ? "color-mix(in oklch, var(--success) 70%, white)"
            : "color-mix(in oklch, var(--destructive) 30%, white)";
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => !disabled && onVote(it.key)}
            disabled={disabled}
            style={{
              padding: "14px 12px",
              border: "none",
              cursor: disabled ? "not-allowed" : "pointer",
              background: isChosen ? accent : "var(--card)",
              color: isChosen && side ? "white" : "var(--foreground)",
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background .18s, color .18s",
              opacity: disabled && !isChosen ? 0.5 : 1,
            }}
          >
            {side && it.side === "left" && <SideChip side="L" isChosen={isChosen} />}
            {it.label}
            {side && it.side === "right" && <SideChip side="R" isChosen={isChosen} />}
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                opacity: 0.6,
                padding: "1px 5px",
                borderRadius: 4,
                border: `1px solid ${isChosen ? "rgba(255,255,255,0.3)" : "var(--border)"}`,
              }}
            >
              {it.hint}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SideChip({ side, isChosen }: { side: "L" | "R"; isChosen: boolean }) {
  return (
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        background: isChosen ? "rgba(255,255,255,0.25)" : "var(--secondary)",
        color: isChosen ? "white" : "var(--muted-foreground)",
        border: isChosen ? "none" : "1px solid var(--border)",
        display: "grid",
        placeItems: "center",
        fontFamily: "var(--font-mono)",
        fontWeight: 700,
        fontSize: 11,
      }}
    >
      {side}
    </span>
  );
}
